import {
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHeader,
  TableHead,
  Table,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { ShimmerLoader } from "./ShimmerLoader";

const StyledTableRoot: React.FC<React.ComponentProps<typeof Table>> = ({
  className,
  ...props
}) => <div className={cn("base-table-styles", className)} {...props} />;

const StyledTable: React.FC<React.ComponentProps<typeof Table>> = ({
  className,
  ...props
}) => <Table className={cn("base-table-styles", className)} {...props} />;

const StyledTableHeader: React.FC<React.ComponentProps<typeof TableHeader>> = ({
  className,
  ...props
}) => (
  <TableHeader
    className={cn("sticky top-0 z-10 bg-brand-surface", className)}
    {...props}
  />
);

const StyledTableBody: React.FC<React.ComponentProps<typeof TableBody>> = ({
  className,
  ...props
}) => (
  <TableBody className={cn("base-table-body-styles", className)} {...props} />
);

const StyledTableFooter: React.FC<React.ComponentProps<typeof TableFooter>> = ({
  className,
  ...props
}) => (
  <TableFooter
    className={cn("base-table-footer-styles", className)}
    {...props}
  />
);

const StyledTableRow = React.forwardRef<
  HTMLTableRowElement,
  React.ComponentProps<typeof TableRow>
>(({ className, ...props }, ref) => (
  <TableRow
    ref={ref}
    className={cn("base-table-row-styles", className)}
    {...props}
  />
));

StyledTableRow.displayName = "StyledTableRow";

const StyledTableHead: React.FC<React.ComponentProps<typeof TableHead>> = ({
  className,
  ...props
}) => (
  <TableHead
    className={cn(
      "font-mono text-[11px] uppercase leading-5 tracking-[0.18em] text-brand-muted",
      className
    )}
    {...props}
  />
);

const StyledTableCell: React.FC<React.ComponentProps<typeof TableCell>> = ({
  className,
  ...props
}) => (
  <TableCell
    className={cn(
      "text-[13px] leading-[19.36px] text-brand-text",
      className
    )}
    {...props}
  />
);

const StyledTableCaption: React.FC<
  React.ComponentProps<typeof TableCaption>
> = ({ className, ...props }) => (
  <TableCaption
    className={cn("base-table-caption-styles", className)}
    {...props}
  />
);

interface ShimmerTableProps<T> {
  columns: ColumnDef<T>[];
  rowCount: number;
  rowsOnly?: boolean;
}

const ShimmerTable = <T,>({
  columns,
  rowCount,
  rowsOnly = false,
}: ShimmerTableProps<T>) => {
  if (rowsOnly) {
    return (
      <>
        {Array.from({ length: rowCount }).map((_, index) => (
          <StyledTableRow
            className={cn({
              "px-14": index === 0 || index === rowCount - 1,
            })}
            key={index}
          >
            {columns.map((_, cellIndex) => (
              <StyledTableCell
                key={cellIndex}
                className={cn({
                  "px-14": cellIndex === 0 || cellIndex === columns.length - 1,
                })}
              >
                <ShimmerLoader height={20} width={100} />
              </StyledTableCell>
            ))}
          </StyledTableRow>
        ))}
      </>
    );
  }
  return (
    <StyledTable>
      <StyledTableHeader>
        <StyledTableRow>
          {columns.map((column, index) => (
            <StyledTableHead key={index}>
              {typeof column.header === "string" ? column.header : null}
            </StyledTableHead>
          ))}
        </StyledTableRow>
      </StyledTableHeader>
      <StyledTableBody className="border-b-[0.5px] border-brand-border text-center align-middle">
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <StyledTableRow
            key={rowIndex}
            className="h-20 border-b-[0.5px] border-brand-border leading-[25px] "
          >
            {columns.map((_, cellIndex) => (
              <StyledTableCell key={cellIndex}>
                <ShimmerLoader height={20} width={160} />
              </StyledTableCell>
            ))}
          </StyledTableRow>
        ))}
      </StyledTableBody>
    </StyledTable>
  );
};

export {
  ShimmerTable,
  StyledTableBody,
  StyledTableRoot,
  StyledTableCaption,
  StyledTableCell,
  StyledTableFooter,
  StyledTableHeader,
  StyledTableHead,
  StyledTableRow,
  StyledTable,
};
